import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, Check, ChevronDown } from 'lucide-react'
import clsx from 'clsx'
import { supportedLanguages, type SupportedLanguage } from '../../i18n/config'
import { useAppStore } from '../../store/useAppStore'

/**
 * Sélecteur de langue compact pour la barre supérieure de l'application.
 * Synchronise i18next, le store global et — si l'utilisateur est connecté —
 * son profil. Les 6 langues du système sont exposées.
 */
export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { i18n } = useTranslation()
  const setLanguage = useAppStore((s) => s.setLanguage)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current =
    supportedLanguages.find((l) => i18n.language?.startsWith(l.code)) ?? supportedLanguages[0]

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const choose = (code: SupportedLanguage) => {
    void i18n.changeLanguage(code)
    setLanguage(code)
    document.documentElement.lang = code
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-night-300 transition-colors hover:bg-white/5 hover:text-white"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Changer de langue / Change language"
      >
        <Globe className="h-[18px] w-[18px]" aria-hidden />
        {!compact && <span className="hidden font-medium uppercase sm:inline">{current.code}</span>}
        <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {open && (
        <ul
          role="listbox"
          className="card absolute right-0 z-50 mt-2 w-44 overflow-hidden p-1 shadow-lift animate-fade-in"
        >
          {supportedLanguages.map((l) => {
            const active = l.code === current.code
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => choose(l.code)}
                  className={clsx(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors',
                    active ? 'bg-primary-500/15 text-primary-300' : 'text-night-200 hover:bg-white/5 hover:text-white'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-7 text-xs font-semibold uppercase text-night-400">{l.code}</span>
                    {l.nativeName}
                  </span>
                  {active && <Check className="h-4 w-4 text-primary-400" aria-hidden />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
